import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
  type VisibilityState,
} from '@tanstack/react-table';
import { Button, Checkbox, ErrorState, Icon, Skeleton } from '@/components/ui';
import { MAX_SORT_KEYS, type CursorPage, type CursorPager, type SortEntry } from './pager';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Allowlisted API sort key. Columns without one are not sortable. */
    sortKey?: string;
    /** Never hideable (identity columns). */
    pinned?: boolean;
    width?: number | string;
    align?: 'left' | 'right';
    /** Phantom use so both generics are referenced. */
    _types?: [TData, TValue];
  }
}

export interface SelectionModel {
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** Return a reason string when the row cannot be selected. */
  ineligibleReason?: (id: string) => string | undefined;
}

export interface DataTableProps<T> {
  caption: string;
  columns: ColumnDef<T, unknown>[];
  getRowId: (row: T) => string;
  query: {
    data: CursorPage<T> | undefined;
    error: unknown;
    isPending: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => unknown;
  };
  pager: CursorPager;
  sort: readonly SortEntry[];
  onSortChange: (next: SortEntry[]) => void;
  selection?: SelectionModel;
  onOpenRow?: (row: T) => void;
  /** Open the row on a single click (outside links and controls); default is double click. */
  openOnClick?: boolean;
  /** Filter controls rendered in the toolbar. */
  toolbar?: ReactNode;
  /** Actions shown while rows are selected. */
  bulkActions?: ReactNode;
  empty: ReactNode;
  /** Persist column visibility under this key (personal preference, column ids only). */
  storageKey?: string;
  defaultHidden?: string[];
  rowLabel?: (row: T) => string;
}

function loadVisibility(key: string | undefined, defaults: string[] | undefined): VisibilityState {
  const base: VisibilityState = Object.fromEntries((defaults ?? []).map((c) => [c, false]));
  if (!key) return base;
  try {
    const raw = window.localStorage.getItem(`eryce.cols.${key}`);
    return raw ? { ...base, ...(JSON.parse(raw) as VisibilityState) } : base;
  } catch {
    return base;
  }
}

export function DataTable<T>({
  caption,
  columns,
  getRowId,
  query,
  pager,
  sort,
  onSortChange,
  selection,
  onOpenRow,
  openOnClick = false,
  toolbar,
  bulkActions,
  empty,
  storageKey,
  defaultHidden,
  rowLabel,
}: DataTableProps<T>) {
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const [visibility, setVisibility] = useState<VisibilityState>(() => loadVisibility(storageKey, defaultHidden));
  const [focusIndex, setFocusIndex] = useState(0);
  const [colsOpen, setColsOpen] = useState(false);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const colsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(`eryce.cols.${storageKey}`, JSON.stringify(visibility));
    } catch {
      /* preference only */
    }
  }, [storageKey, visibility]);

  useEffect(() => {
    if (!colsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!colsRef.current?.contains(e.target as Node)) setColsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colsOpen]);

  const pageIds = useMemo(() => rows.map(getRowId), [rows, getRowId]);
  const eligibleIds = useMemo(
    () => (selection ? pageIds.filter((id) => !selection.ineligibleReason?.(id)) : []),
    [pageIds, selection],
  );
  const selectedOnPage = selection ? pageIds.filter((id) => selection.selected.has(id)).length : 0;
  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selection?.selected.has(id));

  // No React Compiler in this app; TanStack Table's mutable instance is used as designed.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    state: { columnVisibility: visibility },
    onColumnVisibilityChange: setVisibility,
  });

  const toggle = (id: string) => {
    if (!selection || selection.ineligibleReason?.(id)) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  };
  const toggleAllOnPage = () => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allEligibleSelected) eligibleIds.forEach((id) => next.delete(id));
    else eligibleIds.forEach((id) => next.add(id));
    selection.onChange(next);
  };

  const onSortClick = (key: string, additive: boolean) => {
    const existing = sort.find((s) => s.key === key);
    if (!additive) {
      if (!existing) onSortChange([{ key, desc: false }]);
      else if (!existing.desc) onSortChange([{ key, desc: true }]);
      else onSortChange([]);
      return;
    }
    if (!existing) {
      if (sort.length >= MAX_SORT_KEYS) return;
      onSortChange([...sort, { key, desc: false }]);
    } else if (!existing.desc) onSortChange(sort.map((s) => (s.key === key ? { ...s, desc: true } : s)));
    else onSortChange(sort.filter((s) => s.key !== key));
  };

  const focusRow = (i: number) => {
    const clamped = Math.max(0, Math.min(rows.length - 1, i));
    setFocusIndex(clamped);
    bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-row]')[clamped]?.focus();
  };

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, i: number, row: T) => {
    if (e.target !== e.currentTarget) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(i + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(rows.length - 1);
        break;
      case 'Enter':
        if (onOpenRow) {
          e.preventDefault();
          onOpenRow(row);
        }
        break;
      case ' ':
        if (selection) {
          e.preventDefault();
          toggle(getRowId(row));
        }
        break;
    }
  };

  const visibleColumns = table.getVisibleLeafColumns();
  const colCount = visibleColumns.length + (selection ? 1 : 0);
  const loadedFrom = pager.pageIndex * pager.pageSize + 1;
  const hasMore = query.data?.page.has_more ?? false;
  const total = query.data?.page.total;

  return (
    <section className="dt" aria-label={caption}>
      {toolbar ? (
      <div className="dt-toolbar">
        {toolbar}
        <div className="grow" />
        {query.isFetching && !query.isPending ? (
          <span className="row xsmall faint" role="status">
            <span className="spinner" aria-hidden /> Updating
          </span>
        ) : null}
        <div ref={colsRef} style={{ position: 'relative' }}>
          <Button
            size="sm"
            variant="quiet"
            icon="columns"
            aria-expanded={colsOpen}
            aria-haspopup="true"
            onClick={() => setColsOpen((o) => !o)}
          >
            Columns
          </Button>
          {colsOpen ? (
            <div className="popover dt-columns-menu" role="group" aria-label="Visible columns">
              {table
                .getAllLeafColumns()
                .filter((c) => !c.columnDef.meta?.pinned)
                .map((c) => (
                  <Checkbox
                    key={c.id}
                    label={typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id}
                    checked={c.getIsVisible()}
                    onChange={c.getToggleVisibilityHandler()}
                  />
                ))}
            </div>
          ) : null}
        </div>
      </div>
      ) : null}

      {selection && selection.selected.size > 0 ? (
        <div className="dt-selection" role="status">
          <span>
            Selected across pages: <strong className="num">{selection.selected.size}</strong>; current page:{' '}
            <strong className="num">{selectedOnPage}</strong>
          </span>
          <div className="row">{bulkActions}</div>
          <Button size="sm" variant="quiet" onClick={() => selection.onChange(new Set())}>
            Clear selection
          </Button>
        </div>
      ) : null}

      <div className="dt-scroll" tabIndex={-1}>
        <table className="dt-table" aria-busy={query.isPending || undefined} aria-rowcount={rows.length ? rows.length + 1 : undefined}>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {selection ? (
                  <th className="dt-col-select" scope="col">
                    <input
                      type="checkbox"
                      aria-label={`Select eligible rows, current page (${eligibleIds.length})`}
                      disabled={eligibleIds.length === 0}
                      checked={allEligibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = selectedOnPage > 0 && !allEligibleSelected;
                      }}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                ) : null}
                {hg.headers.map((h) => {
                  const meta = h.column.columnDef.meta;
                  const sortKey = meta?.sortKey;
                  const sortIdx = sortKey ? sort.findIndex((s) => s.key === sortKey) : -1;
                  const active = sortIdx >= 0 ? sort[sortIdx] : undefined;
                  const ariaSort = active ? (active.desc ? 'descending' : 'ascending') : sortKey ? 'none' : undefined;
                  const label = flexRender(h.column.columnDef.header, h.getContext());
                  return (
                    <th
                      key={h.id}
                      scope="col"
                      aria-sort={ariaSort}
                      style={{ width: meta?.width, textAlign: meta?.align }}
                    >
                      {sortKey ? (
                        <button
                          type="button"
                          className="dt-sort"
                          onClick={(e) => onSortClick(sortKey, e.shiftKey)}
                          title="Sort. Shift click adds a secondary sort (up to three)."
                        >
                          {label}
                          {active ? (
                            <>
                              <Icon name="chevronDown" size={12} style={{ transform: active.desc ? undefined : 'rotate(180deg)' }} />
                              {sort.length > 1 ? <span className="dt-sort-index">{sortIdx + 1}</span> : null}
                            </>
                          ) : null}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody ref={bodyRef}>
            {query.isPending ? (
              Array.from({ length: 8 }, (_, i) => (
                <tr key={i} className="dt-skeleton-row" aria-hidden>
                  {Array.from({ length: colCount }, (__, j) => (
                    <td key={j}>
                      <Skeleton width={j === 0 && selection ? 14 : `${60 + ((i + j) * 13) % 35}%`} />
                    </td>
                  ))}
                </tr>
              ))
            ) : query.isError && !query.data ? (
              <tr>
                <td colSpan={colCount} style={{ height: 'auto', whiteSpace: 'normal' }}>
                  <ErrorState error={query.error} onRetry={() => void query.refetch()} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ height: 'auto', whiteSpace: 'normal' }}>
                  {empty}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((r, i) => {
                const id = r.id;
                const isSel = selection?.selected.has(id) ?? false;
                const reason = selection?.ineligibleReason?.(id);
                return (
                  <tr
                    key={id}
                    data-row
                    data-selected={isSel || undefined}
                    data-focused={i === focusIndex || undefined}
                    tabIndex={i === Math.min(focusIndex, rows.length - 1) ? 0 : -1}
                    aria-selected={selection ? isSel : undefined}
                    aria-label={rowLabel ? rowLabel(r.original) : undefined}
                    onFocus={() => setFocusIndex(i)}
                    onKeyDown={(e) => onRowKeyDown(e, i, r.original)}
                    data-clickable={openOnClick && onOpenRow ? true : undefined}
                    onClick={
                      openOnClick
                        ? (e) => {
                            if (!(e.target as Element).closest('a, button, input, select, textarea, label')) onOpenRow?.(r.original);
                          }
                        : undefined
                    }
                    onDoubleClick={openOnClick ? undefined : () => onOpenRow?.(r.original)}
                  >
                    {selection ? (
                      <td className="dt-col-select">
                        <input
                          type="checkbox"
                          tabIndex={-1}
                          aria-label={`Select ${rowLabel ? rowLabel(r.original) : id}`}
                          checked={isSel}
                          disabled={Boolean(reason)}
                          title={reason}
                          onChange={() => toggle(id)}
                        />
                      </td>
                    ) : null}
                    {r.getVisibleCells().map((c) => (
                      <td key={c.id} style={{ textAlign: c.column.columnDef.meta?.align }}>
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pager.canPrev || hasMore ? (
        <footer className="dt-footer">
          <span role="status">
            {rows.length > 0 ? (
              <>
                {loadedFrom} to {loadedFrom + rows.length - 1}
                {typeof total === 'number' ? <> of {total}</> : null}
              </>
            ) : null}
          </span>
          <div className="row">
            <Button size="sm" variant="quiet" icon="chevronLeft" disabled={!pager.canPrev} onClick={pager.prev}>
              Previous
            </Button>
            <Button size="sm" variant="quiet" disabled={!hasMore} onClick={() => pager.next(query.data?.page.next_cursor)}>
              Next <Icon name="chevronRight" size={14} />
            </Button>
          </div>
        </footer>
      ) : null}
    </section>
  );
}
